/** Uploads selected files directly to R2 using short-lived Rails-issued URLs. */
class FileUploadManager {
  static pick(options = {}) {
    const input = document.createElement('input');
    input.type = 'file';
    input.style.display = 'none';
    input.accept = options.imagesOnly ? 'image/*' : 'image/*,video/*';
    input.multiple = Boolean(options.multiple);
    document.body.appendChild(input);
    input.addEventListener('change', () => {
      FileUploadManager.upload(input.files, options);
      input.remove();
    }, { once: true });
    input.addEventListener('cancel', () => input.remove(), { once: true });
    input.click();
  }

  static async upload(files, options) {
    if (!files || files.length === 0) { return; }
    options.onFileUploadStart && options.onFileUploadStart();

    try {
      const max = options.multipleMax || Infinity;
      const selected = Array.from(files).slice(0, max);
      const uploaded = await Promise.all(selected.map(file => FileUploadManager.uploadFile(file, options.purpose)));
      options.onFileUploadSuccess && options.onFileUploadSuccess(options.multiple ? uploaded : uploaded[0]);
    } catch (error) {
      const uploadError = new Error(I18n.t('account.profile.notice.medium_upload_error'));
      uploadError.cause = error;
      options.onFileUploadError && options.onFileUploadError(uploadError);
    }
  }

  static async uploadFile(file, purpose) {
    const csrfMeta = document.querySelector('meta[name="csrf-token"]');
    const csrfToken = csrfMeta && csrfMeta.content;
    const response = await fetch('/uploads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify({ 'content_type': file.type, size: file.size, name: file.name, purpose })
    });
    if (!response.ok) { throw new Error('Unable to prepare upload'); }

    const result = await response.json();
    const uploaded = await fetch(result.upload_url, {
      method: 'PUT',
      headers: { 'Content-Type': file.type },
      body: file
    });
    if (!uploaded.ok) { throw new Error('Unable to upload file'); }
    if (result.isVideo) { result.poster = await FileUploadManager.uploadVideoPoster(file); }
    return result;
  }

  static async uploadVideoPoster(file) {
    const objectUrl = URL.createObjectURL(file);
    try {
      const video = document.createElement('video');
      video.muted = true;
      video.preload = 'auto';
      video.src = objectUrl;
      await new Promise((resolve, reject) => {
        const timeout = setTimeout(() => reject(new Error('Video preview timed out')), 10000);
        video.addEventListener('loadeddata', () => { clearTimeout(timeout); resolve(); }, { once: true });
        video.addEventListener('error', () => { clearTimeout(timeout); reject(new Error('Video preview failed')); }, { once: true });
        video.load();
      });
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d').drawImage(video, 0, 0);
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
      if (!blob) { return ''; }
      const posterFile = new File([blob], `${file.name}.jpg`, { type: 'image/jpeg' });
      const poster = await FileUploadManager.uploadFile(posterFile);
      return poster.cdnUrl;
    } catch (_error) {
      return '';
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }
}

export default FileUploadManager;
